/**
 * Parametri di input per il calcolo del ROI.
 *
 * Tutti i valori monetari sono in Euro, i tassi in [0..1] o percentuali
 * come specificato dai singoli campi.
 */
export interface HotelParams {
  actualSystemCost: number;  // C_cybhotel attuale, opzionale, solo per info
  /** Numero di dipendenti (opzionale, solo per info) */
  employeesCount: number;
  /** N — numero di stanze dell'hotel */
  rooms: number;
  /** O — occupazione media attuale, frazione 0..1 */
  occupancy: number;
  /** T_manual — ore di lavoro manuale per stanza/giorno (h) */
  manualMinutesPerRoom: number;
  /** OTA% — percentuale di prenotazioni provenienti da OTA, frazione 0..1 */
  otaPercentage: number;
  /** OTA_comm — commissione media pagata agli OTA, frazione 0..1 */
  otaCommission: number;
  /** D — giorni di esercizio all'anno */
  daysPerYear: number;
  /** C_h — costo orario del lavoro (€) */
  hourlyCost: number;
  /** ADR — Average Daily Rate (€) */
  adr: number;
  /** ΔO — incremento atteso di occupazione, frazione 0..1 */
  deltaOccupancy: number;
  /** C_cybhotel — costo annuo del sistema integrato (€) */
  systemAnnualCost: number;
  /** % incremento atteso di TRevPAR (opzionale) */
  upsellingIncrease: number;
  /** % incremento atteso di TRevPAR (opzionale) */
  directOccupancyIncrease: number;

  //T_admin — ore di lavoro amministrativo per giorno(h)
  manualMinutesAdministrative: number; // 
}

/**
 * Default ragionevoli per un hotel "small" (~20 stanze).
 * Usato come fallback iniziale e dalla pagina Impostazioni → Reset.
 */
export const DEFAULT_PARAMS: HotelParams = {
  manualMinutesAdministrative: 60,
  actualSystemCost: 0,
  otaPercentage: 0.75,
  otaCommission: 0.15,
  rooms: 20,
  occupancy: 0.65,
  manualMinutesPerRoom: 15,
  daysPerYear: 365,
  hourlyCost: 18,
  adr: 95,
  deltaOccupancy: 0.05,
  systemAnnualCost: 2000,
  upsellingIncrease: 0.04,
  directOccupancyIncrease: 0.07,
  employeesCount: 4
};

/**
 * Scenari precompilati richiesti dai casi d'uso del prompt.
 */
export const SCENARIOS: Record<'tiny' | 'small' | 'medium' | 'large', { label: string; description: string; params: HotelParams }> = {
  tiny: {
    label: 'Tiny · 7 stanze',
    description: 'Boutique hotel o B&B, gestione familiare.',
    params: {
      manualMinutesAdministrative: 60,
      actualSystemCost: 0,
      employeesCount: 2,
      rooms: 7,
      occupancy: 0.65,
      manualMinutesPerRoom: 10,
      daysPerYear: 365,
      hourlyCost: 10,
      adr: 80,
      deltaOccupancy: 0.02,
      systemAnnualCost: 1000,
      upsellingIncrease: 0.002,
      directOccupancyIncrease: 0.01,
      otaCommission: 0.15,
      otaPercentage: 0.75,
    },
  },
  small: {
    label: 'Small · 20 stanze',
    description: 'Boutique hotel o B&B, gestione familiare.',
    params: {
      manualMinutesAdministrative: 80,
      actualSystemCost: 0,
      employeesCount: 5,
      rooms: 20,
      occupancy: 0.65,
      manualMinutesPerRoom: 15,
      daysPerYear: 365,
      hourlyCost: 18,
      adr: 95,
      deltaOccupancy: 0.05,
      systemAnnualCost: 2000,
      upsellingIncrease: 0.004,
      directOccupancyIncrease: 0.05,
      otaCommission: 0.15,
      otaPercentage: 0.75,
    },
  },
  medium: {
    label: 'Medium · 80 stanze',
    description: 'Hotel urbano di medie dimensioni, reception attiva 24/7.',
    params: {
      manualMinutesAdministrative: 120,
      actualSystemCost: 5000,
      employeesCount: 15,
      rooms: 80,
      occupancy: 0.72,
      manualMinutesPerRoom: 20,
      daysPerYear: 365,
      hourlyCost: 17,
      adr: 130,
      deltaOccupancy: 0.06,
      systemAnnualCost: 18000,
      upsellingIncrease: 0.005,
      directOccupancyIncrease: 0.06,
      otaCommission: 0.15,
      otaPercentage: 0.75
    },
  },
  large: {
    label: 'Large · 200 stanze',
    description: 'Grand hotel o resort, alto volume di prenotazioni.',
    params: {
      manualMinutesAdministrative: 160,
      actualSystemCost: 15000,
      employeesCount: 40,
      rooms: 200,
      occupancy: 0.78,
      manualMinutesPerRoom: 30,
      daysPerYear: 365,
      hourlyCost: 16,
      adr: 165,
      deltaOccupancy: 0.07,
      systemAnnualCost: 42000,
      upsellingIncrease: 0.006,
      directOccupancyIncrease: 0.07,
      otaCommission: 0.15,
      otaPercentage: 0.75
    },
  },
};

/**
 * Moltiplicatori per gli scenari comparativi (conservativo / realistico / aggressivo).
 * Vengono applicati ai benefici attesi (ore risparmiate, ΔO, TRevPAR %).
 */
export const SCENARIO_FACTORS = {
  conservative: { savings: 0.7, deltaO: 0.6, trevpar: 0.6 },
  realistic: { savings: 1.0, deltaO: 1.0, trevpar: 1.0 },
  aggressive: { savings: 1.3, deltaO: 1.4, trevpar: 1.5 },
} as const;

export type ScenarioKey = keyof typeof SCENARIO_FACTORS;
